import React, { lazy, Suspense, useState } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Compass, LogIn, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TID } from "@/lib/testIds";
import { useAuth } from "@/context/AuthContext";
import { loginHref, registerHref } from "@/lib/authReturn";

const RadioHubModal = lazy(() => import("@/components/hero/RadioHubModal"));

/**
 * The Songwriter Square logo IS the navigation.
 * Four transparent corner hotspots are overlaid on the artwork:
 *   TL → Music Industry
 *   TR → Songwriters
 *   BL → Merchandising
 *   BR → Services
 *
 * The center cross (45%–55% on both axes) is intentionally left free
 * so the vinyl / SS monogram / wordmark artwork stays non-clickable.
 * Hotspots scale proportionally with the image at every viewport size.
 */

const HOTSPOTS = [
  {
    // TOP-LEFT quadrant on the authoritative artwork is labelled SONGWRITERS.
    to: "/songwriters",
    label: "Open Songwriters",
    caption: "The Writers' Corner",
    testid: TID.landing.directionSongwriters,
    style: { top: "4%", left: "3%", width: "43%", height: "37%" },
    captionAnchor: "top-left",
  },
  {
    // TOP-RIGHT quadrant is labelled MUSIC INDUSTRY.
    to: "/music-industry",
    label: "Open Music Industry",
    caption: "The Industry House",
    testid: TID.landing.directionMusicIndustry,
    style: { top: "4%", right: "3%", width: "43%", height: "37%" },
    captionAnchor: "top-right",
  },
  {
    // BOTTOM-LEFT is labelled MERCHANDISE.
    to: "/merchandising",
    label: "Open Merchandise",
    caption: "The Market Row",
    testid: TID.landing.directionMerchandising,
    style: { top: "42%", left: "3%", width: "43%", height: "37%" },
    captionAnchor: "bottom-left",
  },
  {
    // BOTTOM-RIGHT is labelled SERVICES.
    to: "/services",
    label: "Open Services",
    caption: "The Craft House",
    testid: TID.landing.directionServices,
    style: { top: "42%", right: "3%", width: "43%", height: "37%" },
    captionAnchor: "bottom-right",
  },
];

// Where the contextual gold caption anchors within its hotspot on hover/focus.
const CAPTION_POS = {
  "top-left":     "top-1 left-1",
  "top-right":    "top-1 right-1",
  "bottom-left":  "bottom-1 left-1",
  "bottom-right": "bottom-1 right-1",
};

export default function CompassHero({ onScrollToLearn }) {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [radioOpen, setRadioOpen] = useState(false);

  return (
    <section
      data-testid={TID.landing.root}
      className="relative hero-vignette overflow-hidden"
    >
      <div className="max-w-5xl mx-auto px-6 pt-16 pb-16 md:pt-24 md:pb-24">
        {/* Logo IS the navigation. Corners are the buttons. */}
        <div
          data-testid="compass-image-nav"
          className="relative mx-auto w-full max-w-[560px] aspect-[750/755]"
        >
          <motion.img
            src="/logo.png"
            alt="Songwriter Square"
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-0 h-full w-full object-contain select-none drop-shadow-[0_0_60px_rgba(212,175,55,0.35)]"
            draggable={false}
            data-testid={TID.landing.logo}
          />

          {/* Four transparent corner hotspots — non-overlapping, centered cross free */}
          {HOTSPOTS.map((h, i) => (
            <motion.button
              key={h.to}
              type="button"
              onClick={() => navigate(h.to)}
              aria-label={h.label}
              data-testid={h.testid}
              disabled={radioOpen}
              style={{ ...h.style, zIndex: 10, pointerEvents: radioOpen ? "none" : "auto" }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.5 + i * 0.08 }}
              className="absolute cursor-pointer bg-transparent border-0 p-0 group focus:outline-none disabled:cursor-default"
            >
              {/* Soft gold radial glow (hover / focus) */}
              <span
                aria-hidden="true"
                className="absolute inset-2 rounded-sm opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity duration-500 ease-out bg-[radial-gradient(circle_at_center,rgba(212,175,55,0.28),transparent_70%)]"
              />
              {/* Focus ring for keyboard users */}
              <span
                aria-hidden="true"
                className="absolute inset-2 rounded-sm border border-transparent group-focus-visible:border-gold group-focus-visible:shadow-goldSoft transition-all duration-200"
              />
              {/* Subtle brightness lift */}
              <span
                aria-hidden="true"
                className="absolute inset-0 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity duration-500 ease-out bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.06),transparent_60%)]"
              />
              {/* Elegant gold caption revealed on hover/focus (contextual name) */}
              <span
                aria-hidden="true"
                data-testid={`${h.testid}-caption`}
                className={`absolute ${CAPTION_POS[h.captionAnchor]} m-2 px-2.5 py-1 border border-gold/60 bg-black/70 backdrop-blur-sm text-gold text-[9px] uppercase tracking-[0.3em] font-display whitespace-nowrap opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 translate-y-1 group-hover:translate-y-0 group-focus-visible:translate-y-0 transition-all duration-300`}
              >
                {h.caption}
              </span>
            </motion.button>
          ))}

          {/*
            CENTER SS RECORD HOTSPOT — 12% diameter circle at exact centre.
            Corner boxes are 45% x 45% at each corner; the nearest corner of any
            corner box lives at (45%, 45%) from the image origin — its distance
            from the image centre (50%, 50%) is sqrt(5% * 5% * 2) ≈ 7.07%, which
            is greater than this circle's 6% radius, so no overlap occurs.
            zIndex=20 places this ABOVE the corner hotspots, so the click never
            gets intercepted by them.
          */}
          <motion.button
            type="button"
            onClick={() => setRadioOpen(true)}
            aria-label="Open Songwriter Square Radio"
            data-testid={TID.radioHotspot}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.9 }}
            style={{
              position: "absolute",
              top: "41%",
              left: "50%",
              width: "18%",
              height: "18%",
              transform: "translate(-50%, -50%)",
              zIndex: 20,
            }}
            className="rounded-full cursor-pointer bg-transparent border-0 p-0 group focus:outline-none"
          >
            {/* Soft radial gold glow */}
            <span
              aria-hidden="true"
              className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity duration-500 ease-out bg-[radial-gradient(circle_at_center,rgba(212,175,55,0.42),transparent_70%)]"
            />
            {/* Subtle brightness lift */}
            <span
              aria-hidden="true"
              className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity duration-500 ease-out bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.09),transparent_60%)]"
            />
            {/* Keyboard focus ring */}
            <span
              aria-hidden="true"
              className="absolute inset-0 rounded-full border border-transparent group-focus-visible:border-gold group-focus-visible:shadow-goldGlow transition-all duration-200"
            />
            {/* Contextual caption for the center hotspot */}
            <span
              aria-hidden="true"
              data-testid={`${TID.radioHotspot}-caption`}
              className="absolute left-1/2 -translate-x-1/2 -bottom-8 px-2.5 py-1 border border-gold/60 bg-black/75 backdrop-blur-sm text-gold text-[9px] uppercase tracking-[0.3em] font-display whitespace-nowrap opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 translate-y-1 group-hover:translate-y-0 group-focus-visible:translate-y-0 transition-all duration-300"
            >
              Songwriter Square Radio
            </span>
          </motion.button>
        </div>

        {/* Entry buttons — directly beneath the logo */}
        <motion.div
          data-testid="hero-entry-actions"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-8 mx-auto w-full max-w-[560px]"
        >
          {isAuthenticated ? (
            <Button
              asChild
              className="rounded-none h-12 w-full sm:w-auto sm:min-w-[280px] sm:mx-auto sm:flex px-10 bg-goldSheen text-black font-display uppercase tracking-[0.25em] text-xs hover:brightness-110 shadow-goldSoft hover:shadow-goldGlow border border-gold/60 transition-[filter,box-shadow] duration-300"
            >
              <Link to="/app/dashboard" data-testid="hero-enter-square-btn">
                <ArrowRight className="h-4 w-4 mr-2" /> Enter the Square
              </Link>
            </Button>
          ) : (
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-4">
              <Button
                asChild
                variant="outline"
                className="rounded-none h-12 flex-1 sm:flex-none sm:min-w-[170px] px-8 bg-black/60 border-gold/60 text-gold hover:bg-gold/10 hover:border-gold hover:shadow-goldSoft font-display uppercase tracking-[0.25em] text-xs transition-[background-color,border-color,box-shadow] duration-300"
              >
                <Link to={loginHref("/app/dashboard")} data-testid={TID.landing.signInBtn}>
                  <LogIn className="h-4 w-4 mr-2" /> Sign In
                </Link>
              </Button>
              <Button
                asChild
                className="rounded-none h-12 flex-1 sm:flex-none sm:min-w-[190px] px-8 bg-goldSheen text-black font-display uppercase tracking-[0.25em] text-xs hover:brightness-110 shadow-goldSoft hover:shadow-goldGlow border border-gold/60 transition-[filter,box-shadow] duration-300"
              >
                <Link to={registerHref("/app/dashboard")} data-testid={TID.landing.joinBtn}>
                  <UserPlus className="h-4 w-4 mr-2" /> Create Account
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                className="rounded-none h-12 flex-1 sm:flex-none sm:min-w-[190px] px-8 bg-black/60 border-gold/60 text-gold hover:bg-gold/10 hover:border-gold hover:shadow-goldSoft font-display uppercase tracking-[0.25em] text-xs transition-[background-color,border-color,box-shadow] duration-300"
              >
                <Link to="/app/dashboard" data-testid="hero-tour-guest-btn">
                  <Compass className="h-4 w-4 mr-2" /> Tour as Guest
                </Link>
              </Button>
            </div>
          )}
        </motion.div>

        {/* Tagline (below the entry buttons) */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.45 }}
          className="mt-10 text-center"
        >
          <div className="mx-auto h-px w-24 gold-divider" />
          <p
            data-testid={TID.landing.tagline}
            className="mt-5 text-xs sm:text-sm tracking-[0.5em] uppercase text-white/60 font-display"
          >
            The Song Comes First
          </p>
        </motion.div>

        {/* Subtle "scroll to explore" hint — no account CTAs on the hero.
            All Join / Sign In / Guest actions live in the final section only. */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.55 }}
          className="mt-8 flex flex-wrap items-center justify-center gap-3 sm:gap-4"
        >
          <Button
            variant="ghost"
            data-testid={TID.landing.learnMoreBtn}
            onClick={onScrollToLearn}
            className="rounded-none h-11 px-6 text-white/70 hover:text-gold hover:bg-white/5 font-display uppercase tracking-widest"
          >
            Enter the Square
          </Button>
        </motion.div>
      </div>

      <Suspense fallback={null}>
        <RadioHubModal open={radioOpen} onClose={() => setRadioOpen(false)} />
      </Suspense>
    </section>
  );
}
